import * as Sentry from '@sentry/react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router';
import { z } from 'zod';
import dialog from '~/components/Commons/ActionConfirmDialog/useDialog';
import useNotifications from '~/context/Notifications';
import { useClientSessionQuery } from '~/hooks/queries/useSessionPlQuery';
import { customFetch } from '~/utils/customFetch';

type PostData =
  | {
      partnerId: number;
      amount: number;
      period: string;
      clientEditable: number;
    }
  | {
      period: string;
      amount: number;
      startDate: string | null;
      paced: number;
      clientEditable: number;
    };

const forecastErrorResponseSchema = z.object({
  message: z.string(),
});
export function useCapsMutation() {
  const clientSessionQuery = useClientSessionQuery();
  const queryClient = useQueryClient();
  const { setTimedNotification } = useNotifications();
  const [, setSearchParams] = useSearchParams();

  return useMutation({
    mutationKey: ['overallCaps'],
    mutationFn: async (data: PostData[]) => {
      const res = await customFetch(
        `/authenticated/client/${clientSessionQuery.data?.id}/bookings/create`,
        {
          method: 'POST',
          useNodeEndpoint: true,
          body: JSON.stringify(data),
          throwOnHTTPError: false,
        },
      );
      if (res.status === 200) {
        return res;
      }
      if (res.status >= 400 && res.status < 500) {
        const error = forecastErrorResponseSchema.parse(data);
        setTimedNotification('danger', error.message, 5000);
      } else {
        throw new Error(res.statusText, { cause: res });
      }

      return res;
    },
    onSuccess: () => {
      dialog.close();
      setSearchParams();
    },
    onError: (error: unknown) => {
      setTimedNotification('danger', 'Something went wrong, please try again', 5000);
      Sentry.captureException(error);
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: ['overallCaps'],
      });
    },
  });
}
